import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { APIURL } from "../../../Global";
import "./deathreport.css";
import DeathReportForm from './DeathReportForm';
import ApprovePending from './ApprovePending';
import Rejected from './Rejected';
 
const DeathReport = () => {
    const [selected, setSelected] = useState("form");
    const [patientDetails, setPatientDetails] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [patient, setPatient] = useState([]);
    const [pending, setPending] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [request, setRequest] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
   
    const location = useLocation();
    const navigate = useNavigate();
    const [search, setSearch] = useState(location.state?.pat_id || "");
 
    // Scroll to top on component mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
 
    // Handle navigation state cleanup
    useEffect(() => {
        const isNavigated = sessionStorage.getItem('deathReportSearch') === 'true';
 
        if (isNavigated && location.state?.pat_id) {
            setSearch(location.state.pat_id);
            sessionStorage.removeItem('deathReportSearch');
            navigate(location.pathname, { replace: true });
        } else if (location.state?.pat_id) {
            navigate(location.pathname, { replace: true });
        }
    }, [location.state, navigate]);
 
    // Optimized data sorting function
    const sortData = useCallback((data, searchTerm = search) => {
        const sortedData = data.reduce((acc, item) => {
            switch (item.status) {
                case "rejected":
                    acc.rejected.push(item);
                    break;
                case "pending":
                    acc.pending.push(item);
                    break;
                case "approved":
                    acc.patient.push(item);
                    break;
                default:
                    console.warn(`Unknown status: ${item.status}`);
            }
            return acc;
        }, {
            rejected: [],
            pending: [],
            patient: []
        });
 
        // Update state with sorted data
        setRejected(sortedData.rejected);
        setPending(sortedData.pending);
       
        setPatient( sortedData.patient?.map(p => ({ ...p, applied: false })) || []);
 
        // Auto-select appropriate tab when search is active
        if (searchTerm !== '') {
            if (sortedData.pending.length > 0) {
                setSelected("pending");
            } else if (sortedData.rejected.length > 0) {
                setSelected("rejected");
            } else if (sortedData.patient.length > 0) {
                setSelected("reports");
            } else {
                // No data found for search, stay on current tab or go to form
                setSelected("form");
            }
        }
    }, []);
 
    // Optimized search handler
    const handleSearchChange = useCallback((val, data = request) => {
        setSearch(val);
       
        if (val.trim() === '') {
            sortData(data);
        } else {
            const searchTerm = val.toLowerCase().trim();
            const filteredData = data.filter(({ patient_name, patient }) =>
                patient_name?.toLowerCase().includes(searchTerm) ||
                patient?.toString().toLowerCase().includes(searchTerm)
            );
            sortData(filteredData);
        }
    }, [request, sortData]);
 
    // Fetch death reports data
    useEffect(() => {
        const fetchData = async () => {
            const token = sessionStorage.getItem("usertoken")?.replace(/["]+/g, "");
           
            if (!token) {
                setError("Authentication token not found");
                return;
            }
 
            setIsLoading(true);
            setError(null);
 
            try {
                const response = await axios.get(`${APIURL}/api/v1/service-provider/patient-death/`, {
                    headers: { Authorization: `Token ${token}` },
                });
 
                if (response.data.status === "success") {
                    const fetchedData = response.data.message || [];
                    setRequest(fetchedData);
                   
                    if (search.trim() === '') {
                        sortData(fetchedData);
                    } else {
                        handleSearchChange(search, fetchedData);
                    }
                } else {
                    setError("Failed to fetch data");
                }
            } catch (err) {
                console.error("Error fetching death reports:", err);
                setError(err.response?.data?.message || "Failed to fetch death reports");
            } finally {
                setIsLoading(false);
            }
        };
 
        fetchData();
    }, [refresh]); // Only refresh should trigger API call
 
    // Separate effect to handle search filtering when request data changes
    useEffect(() => {
        if (request.length > 0) {
            if (search.trim() === '') {
                sortData(request);
            } else {
                handleSearchChange(search, request);
            }
        }
    }, [request, search]); // This will run when request data or search changes
   
    const handleRefresh = useCallback(() => {
        setRefresh(prev => !prev);
    }, []);
 
    // Success handlers
    const handleFormSuccess = useCallback(() => {
        setSelected("pending");
        handleRefresh();
    }, [handleRefresh]);
 
    const handleRejectedSuccess = useCallback((data) => {
        setPatientDetails(data);
        setSelected("form");
    }, []);
 
    // Tab configuration
    const tabs = useMemo(() => [
        { key: "form", label: "Form", icon: "user" },
        { key: "pending", label: "Pending", icon: "file" },
        { key: "rejected", label: "Rejected", icon: "file" },
        { key: "reports", label: "Reports", icon: "file" }
    ], []);
 
    // Render content based on selected tab
    const renderContent = () => {
        if (isLoading) {
            return <div className="loading-spinner">Loading...</div>;
        }
 
        if (error) {
            return <div className="error-message">Error: {error}</div>;
        }
 
        switch (selected) {
            case "form":
                return (
                    <DeathReportForm
                        patient={patientDetails}
                        onSuccess={handleFormSuccess}
                    />
                );
            case "pending":
                return (
                    <ApprovePending
                        person={pending}
                        onRefresh={handleRefresh}
                    />
                );
            case "rejected":
                return (
                    <Rejected
                        person={rejected}
                        onSuccess={handleRejectedSuccess}
                        onRefresh={handleRefresh}
                    />
                );
            case "reports":
                return (
                    <ApprovePending
                        person={patient}
                        onRefresh={handleRefresh}
                        setPerson={setPatient}
                    />
                );
            default:
                return <div>Invalid selection</div>;
        }
    };
 
    return (
        <div style={{ paddingLeft: "1rem", paddingRight: "1.5rem" }}>
            <div className='row'>
                <div className="col-lg-2 col-md-2">
                    <h5
                        style={{
                            color: "#245693",
                            marginTop: "8%",
                            fontFamily: "inter",
                            fontWeight: "700",
                            marginLeft: "20px",
                        }}
                    >
                        Death Applications
                    </h5>
                   
                    <div className="leftdivdeathrepo">
                        <div className="searchboxdeathrepo">
                            <input
                                placeholder="Search Name or ID"
                                type="text"
                                value={search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                style={{
                                    marginLeft: '10px',
                                    marginTop: "3px",
                                    border: "0",
                                    outline: "none",
                                    width: "85%"
                                }}
                            />
                        </div>
                       
                        {tabs.map(({ key, label, icon }) => (
                            <button
                                key={key}
                                className={`deathreportbutton ${selected === key ? "active" : ""}`}
                                onClick={() => setSelected(key)}
                                style={{
                                    backgroundColor: selected === key ? "#6D4482" : "#F8F8F8",
                                    color: selected === key ? "white" : "black"
                                }}
                                aria-pressed={selected === key}
                            >
                                <i
                                    className={`fa fa-${icon}`}
                                    aria-hidden="true"
                                    style={{ color: selected === key ? "white" : "#6D4482" }}
                                />
                                &nbsp;&nbsp;{label}
                            </button>
                        ))}
                    </div>
                </div>
               
                <div className="col-lg-10 col-md-10">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};
 
export default DeathReport;
 